import type {
  ArrivalTrackCondition,
  BroadcastCondition,
  BroadcastConditionOperator,
  ConnectingDelayJudgementMode,
  ConnectingLineDelayCondition,
} from '../../../api/types';
import type { RouteMaster } from '../../../data/routes';
import { sortStopsBySequence, type RouteStop } from '../../../data/routeStops';
import { clampNonNegativeInt, NumericInput } from '../../NumericInput';
import { CONNECTING_DELAY_MODE_META, OPERATOR_META } from '../constants';

/**
 * 1 条件行のエディタ。ConnectingLineDelayFields のコンパクト版として振る舞う。
 *
 * 判定モードの切替時には、他モードでしか使わない入力項目 (区間駅・乗換駅・乗換時間・方面) を
 * 明示的にクリアして DB / JSON にゴミが残らないようにする。
 *
 * `condition_type === 'arrival_track'` (到着番線条件) の入力 UI は後続 SBI で実装する。
 * 本 SBI では型 (判別可能ユニオン) を通すための早期分岐のみを置き、
 * 該当条件はプレースホルダ表示 + 削除のみ可能とする。
 */
export function BroadcastConditionRow({
  condition,
  routes,
  routeStops,
  onChange,
  onReplace,
  onRemove,
}: {
  condition: BroadcastCondition;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  onChange: (patch: Partial<ConnectingLineDelayCondition>) => void;
  onReplace: (next: BroadcastCondition) => void;
  onRemove: () => void;
}): React.JSX.Element {
  if (condition.condition_type === 'arrival_track') {
    return <ArrivalTrackConditionRowPlaceholder condition={condition} onRemove={onRemove} />;
  }
  const currentRouteId = condition.route_id ?? '';
  const mode: ConnectingDelayJudgementMode = condition.delay_judgement_mode ?? 'whole_line';
  const operator: BroadcastConditionOperator = condition.operator ?? 'gte';
  const delayMinutes =
    typeof condition.delay_minutes === 'number' && !Number.isNaN(condition.delay_minutes)
      ? condition.delay_minutes
      : 0;

  const isWholeLine = mode === 'whole_line';
  const showSectionFields = mode === 'section_scheduled';
  const showTransferFields = mode === 'transfer_aware';
  const showDirectionField = !isWholeLine;

  // 選択中の接続線 (route_id) に紐づく駅一覧。stop_sequence 昇順に整列する。
  const stops = sortStopsBySequence(currentRouteId ? routeStops[currentRouteId] : []);

  // マスタに無い route_id (古いデータ・別線区参照など) も選択肢に残す。
  const isUnknownRoute =
    currentRouteId !== '' && !routes.some((r) => r.route_id === currentRouteId);

  const currentRoute = routes.find((r) => r.route_id === currentRouteId);
  const directionOptions = (currentRoute?.directions ?? []).map((d, i) => ({
    value: typeof d.direction_id === 'string' && d.direction_id !== '' ? d.direction_id : String(i),
    label: d.direction_name,
  }));
  const directionId = condition.direction_id ?? '';
  const isUnknownDirection =
    directionId !== '' && !directionOptions.some((o) => o.value === directionId);

  const transferTimeSeconds =
    typeof condition.transfer_time_seconds === 'number' &&
    !Number.isNaN(condition.transfer_time_seconds)
      ? condition.transfer_time_seconds
      : 180;

  // 判定モード切替: 不要フィールドを明示的にクリアする。
  const handleModeChange = (nextMode: ConnectingDelayJudgementMode): void => {
    const next: BroadcastCondition = {
      route_id: condition.route_id,
      delay_judgement_mode: nextMode,
      delay_minutes: condition.delay_minutes,
      operator: condition.operator,
    };
    if (nextMode === 'section_scheduled') {
      next.start_stop_id = condition.start_stop_id;
      next.end_stop_id = condition.end_stop_id;
      next.direction_id = condition.direction_id;
    } else if (nextMode === 'transfer_aware') {
      next.transfer_stop_id = condition.transfer_stop_id;
      next.transfer_time_seconds =
        typeof condition.transfer_time_seconds === 'number' ? condition.transfer_time_seconds : 180;
      next.direction_id = condition.direction_id;
    }
    // whole_line のときは start/end_stop_id / transfer_* / direction_id を落とす
    onReplace(next);
  };

  const handleRouteChange = (nextRouteId: string): void => {
    // 路線を変えたら別路線の stop_id / direction_id が残らないようにクリアする。
    onReplace({
      route_id: nextRouteId,
      delay_judgement_mode: mode,
      delay_minutes: delayMinutes,
      operator,
      transfer_time_seconds: showTransferFields ? transferTimeSeconds : undefined,
    });
  };

  return (
    <div className="broadcast-condition-fields" style={{ display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>接続線遅延の条件</strong>
        <button
          type="button"
          onClick={onRemove}
          data-testid="broadcast-condition-remove"
          aria-label="この条件を削除"
        >
          × 削除
        </button>
      </div>

      <label>
        <span className="required">接続線 route_id</span>
        <select
          aria-label="接続線 route_id"
          value={currentRouteId}
          onChange={(e) => handleRouteChange(e.target.value)}
        >
          <option value="">(選択してください)</option>
          {routes.map((r) => (
            <option key={r.route_id} value={r.route_id}>
              {r.route_id}
            </option>
          ))}
          {isUnknownRoute ? (
            <option value={currentRouteId}>{currentRouteId} (マスタ未登録)</option>
          ) : null}
        </select>
      </label>

      <label>
        <span className="required">判定モード</span>
        <select
          aria-label="判定モード"
          value={mode}
          onChange={(e) => handleModeChange(e.target.value as ConnectingDelayJudgementMode)}
        >
          {CONNECTING_DELAY_MODE_META.map((m) => (
            <option key={m.mode} value={m.mode}>
              {m.label}
            </option>
          ))}
        </select>
      </label>

      {showSectionFields ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <label>
            <span className="required">遅延判定開始駅</span>
            <select
              aria-label="遅延判定開始駅"
              value={condition.start_stop_id ?? ''}
              onChange={(e) => onChange({ start_stop_id: e.target.value })}
            >
              <option value="">(選択してください)</option>
              {stops.map((s) => (
                <option key={s.stop_id} value={s.stop_id}>
                  {s.stop_name || s.stop_id}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="required">遅延判定終了駅</span>
            <select
              aria-label="遅延判定終了駅"
              value={condition.end_stop_id ?? ''}
              onChange={(e) => onChange({ end_stop_id: e.target.value })}
            >
              <option value="">(選択してください)</option>
              {stops.map((s) => (
                <option key={s.stop_id} value={s.stop_id}>
                  {s.stop_name || s.stop_id}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {showTransferFields ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <label>
            <span className="required">乗換駅</span>
            <select
              aria-label="乗換駅"
              value={condition.transfer_stop_id ?? ''}
              onChange={(e) => onChange({ transfer_stop_id: e.target.value })}
            >
              <option value="">(選択してください)</option>
              {stops.map((s) => (
                <option key={s.stop_id} value={s.stop_id}>
                  {s.stop_name || s.stop_id}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="required">乗換時間 (秒)</span>
            <NumericInput
              min={0}
              step={1}
              aria-label="乗換時間 (秒)"
              value={transferTimeSeconds}
              normalizeValue={clampNonNegativeInt}
              onChange={(nextTransferTime) => onChange({ transfer_time_seconds: nextTransferTime })}
            />
          </label>
        </div>
      ) : null}

      {showDirectionField ? (
        <label>
          <span>方面 (任意)</span>
          <select
            aria-label="方面"
            value={directionId}
            onChange={(e) => onChange({ direction_id: e.target.value })}
          >
            <option value="">(指定なし)</option>
            {directionOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            {isUnknownDirection ? (
              <option value={directionId}>{directionId} (マスタ未登録)</option>
            ) : null}
          </select>
        </label>
      ) : null}

      <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}>
        <label>
          <span className="required">遅延分数</span>
          <NumericInput
            min={0}
            step={1}
            aria-label="遅延分数"
            value={delayMinutes}
            normalizeValue={clampNonNegativeInt}
            onChange={(nextDelayMinutes) => onChange({ delay_minutes: nextDelayMinutes })}
            style={{ width: 80 }}
          />
        </label>
        <label>
          <span className="required">比較</span>
          <select
            aria-label="比較演算子"
            value={operator}
            onChange={(e) => onChange({ operator: e.target.value as BroadcastConditionOperator })}
          >
            {OPERATOR_META.map((o) => (
              <option key={o.operator} value={o.operator}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <span style={{ color: '#666', fontSize: '0.9em' }}>
          (遅延 {operator === 'gte' ? '≧' : '≦'} {delayMinutes} 分のとき放送)
        </span>
      </div>
    </div>
  );
}

/**
 * 到着番線条件 (`condition_type: 'arrival_track'`) の暫定表示。
 *
 * 入力 UI (番線セレクト / 一致・不一致の切替) は後続 SBI のスコープ。
 * ここでは設定値の確認と削除のみを提供し、既存条件を誤って壊さないようにする。
 */
function ArrivalTrackConditionRowPlaceholder({
  condition,
  onRemove,
}: {
  condition: ArrivalTrackCondition;
  onRemove: () => void;
}): React.JSX.Element {
  const match = condition.arrival_track_match ?? 'eq';
  return (
    <div className="broadcast-condition-fields" style={{ display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>到着番線の条件</strong>
        <button
          type="button"
          onClick={onRemove}
          data-testid="broadcast-condition-remove"
          aria-label="この条件を削除"
        >
          × 削除
        </button>
      </div>
      <p style={{ margin: 0, color: '#666', fontSize: '0.9em' }}>
        到着番線 (stop_id): {condition.arrival_stop_id || '(未設定)'} /{' '}
        {match === 'ne' ? '不一致のとき放送' : '一致するとき放送'}
      </p>
    </div>
  );
}
